import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import SafeSrcDocIframe from 'react-safe-src-doc-iframe';
import aFrame from '../../aframe/aframe.txt';
import { getAnimationUrl, getCardById, getModelUrlById } from '../../assets/cards/cards';
import { ReactComponent as Back } from '../../assets/icons/back.svg';
import { ReactComponent as Photo } from '../../assets/icons/photo.svg';
import PrimaryIconButton from '../../components/buttons/primaryiconbutton/PrimaryIconButton';
import CardPhoto from '../../components/cardphoto/CardPhoto';
import H1 from '../../components/text/H1';
import { tag } from '../../services/ATInternetService';
import { isCardInCollection, searchCardByCardId } from '../../stored/Collection';
import { addToUserActions, hasUserAction } from '../../stored/UserActions';
import Page from '../Page';
import './CardView.sass';
import LockedCard from './lockedcard/LockedCard';
import UnlockedCard from './unlockedcard/UnlockedCard';

const quizTexts = ['Wat denk je?'];

const CardView = ({ onBackPressed, openCollection, scanned, card }) => {
	const [aFrameHTML, setAFrameHTML] = useState(null);
	const [isARVisible, setIsARVisible] = useState(true);
	const [isPhotoVisible, setIsPhotoVisible] = useState(false);
	const [photoShareData, setPhotoShareData] = useState(null);
	const [isQuizQuestionVisible, setIsQuizQuestionVisible] = useState(false);
	const [quizTitleText, setQuizTitleText] = useState(quizTexts[0]);

	let cardId = scanned?.card?.id ?? card?.cardId;

	if (!card && isCardInCollection(cardId)) {
		card = searchCardByCardId(cardId);
		cardId = card.cardId;
	}

	if (!aFrameHTML)
		fetch(aFrame)
			.then((r) => r.text())
			.then((text) => {
				text = text.replace('{{ MODEL }}', getModelUrlById(cardId)).replace('{{ NICKNAME }}', scanned?.card?.nickName || getCardById(card.cardId).nickName).replace('{{ ANIMATION }}', getAnimationUrl());
				setAFrameHTML(text);
			});

	useEffect(() => {
		if (!isARVisible && !hasUserAction(`opened_full_card_${cardId}`)) {
			addToUserActions(`opened_full_card_${cardId}`, {});
		}
	}, [aFrameHTML, isARVisible, cardId]);

	return (
		<>
			<Page className="flex flex-col">
				{(aFrameHTML && isARVisible) ? (
					<SafeSrcDocIframe
						className="flex-grow w-full bg-transparent transition-all"
						style={{ marginBottom: '-2rem' }}
						sandbox="allow-same-origin allow-scripts allow-modals allow-popups"
						allow="accelerometer; gyroscope; magnetometer"
						allowvr="yes"
						allowFullScreen="yes"
						title="3D"
						srcDoc={aFrameHTML}
					/>
				) : (
					<div className="flex-grow" />
				)}

				{isCardInCollection(cardId) ? (
					<UnlockedCard
						card={card}
						foundAt={card.atPlace}
						cardId={card.id}
						score={card.score}
						isExpanded={!isARVisible}
						onRequestHideAR={(shouldHide) => {
							setIsARVisible(!shouldHide);
							if (shouldHide) tag.sendClick('card-view', {
								chapter3: cardId,
								name: 'opened-full-card',
							});
						}}
					/>
				) : (
					<LockedCard
						card={scanned.card}
						foundAt={scanned.match.class}
						openCollection={openCollection}
						onQuizQuestionVisibilityChanged={(visible) => {
							setIsQuizQuestionVisible(visible);
							const currentIndex = quizTexts.indexOf(quizTitleText);
							if (currentIndex < quizTexts.length - 1) {
								setQuizTitleText(quizTexts[currentIndex + 1]);
							} else {
								setQuizTitleText(quizTexts[0]);
							}
						}}
					/>
				)}
			</Page>

			<PrimaryIconButton
				className={isARVisible ? 'absolute top-6 left-6 z-20 transition-all duration-500' : 'absolute top-6 left-1/2 z-20 transition-all duration-500 transform -translate-x-1/2'}
				onClick={() => {
					if (!isARVisible) {
						setIsARVisible(true);
						tag.sendClick('card-view', {
							chapter3: cardId,
							name: 'closed-full-card',
						});
					} else {
						onBackPressed();
						tag.sendClick('card-view', {
							chapter3: cardId,
							name: 'closed',
						});
					}
				}}
				animated={isARVisible}>
				<Back
					className={isARVisible ? 'transition-all duration-500' : 'transition-all duration-500 transform -rotate-90'}
				/>
			</PrimaryIconButton>

			{isCardInCollection(cardId) && <PrimaryIconButton
				className={isARVisible && hasUserAction(`opened_full_card_${cardId}`) ? 'absolute top-6 right-6 z-20 transition-all duration-500' : 'absolute -top-48 right-6 z-20 transition-all duration-500'}
				onClick={() => {
					tag.sendClick('card-view', {
						chapter3: cardId,
						name: 'photo-captured',
					});
					localStorage.setItem('iFrameRequest', 'photo');
					let checkCount = 0;
					const checkForPhoto = async () => {
						// @ts-ignore
						if (window.photo !== undefined && window.photo !== null) {
							// @ts-ignore
							const response = await fetch(window.photo);
							const blob = await response.blob();
							const shareData = {
								files: [
									new File([blob], `Klokhuis Scanner foto ${String(Math.floor(Date.now() / 1000))}.png`, {
										type: blob.type,
									}),
								],
								title: 'Zie mijn foto! https://hetklokhuis.nl/klokhuisscanner',
							};
							setPhotoShareData(shareData);
							setIsPhotoVisible(true);

							// @ts-ignore
							window.playSound('camera_shutter');
						} else if (checkCount < 20) {
							checkCount += 1;
							// eslint-disable-next-line no-unused-vars
							setTimeout(checkForPhoto, 50);
						}
					}
					checkForPhoto();
				}}
				animated={isARVisible}>
				<Photo
					className={isARVisible ? 'transition-all duration-500' : 'transition-all duration-500 transform -rotate-90'}
				/>
			</PrimaryIconButton>}

			{isPhotoVisible && <CardPhoto
				shareData={photoShareData}
				onDone={() => {
					setIsPhotoVisible(false);
					// @ts-ignore
					window.photo = null;
				}}
			/>}

			{<H1 className="text-white absolute left-24 top-9 transition-all duration-1000" shadow xl style={{ opacity: isQuizQuestionVisible ? 1 : 0 }}>{quizTitleText}</H1>}
		</>
	);
};

CardView.propTypes = {
	onBackPressed: PropTypes.func.isRequired,
	openCollection: PropTypes.func.isRequired,
	scanned: PropTypes.shape({
		match: PropTypes.shape({
			class: PropTypes.any
		}),
		card: PropTypes.any
	}),
	card: PropTypes.any
}

CardView.defaultProps = {
	scanned: undefined,
	card: undefined
}

export default CardView;
